import { useState } from 'react';

import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';

import FeaturedIcon from '@webclient/components/featured-icon';
import Generator from '@webclient/components/generate-icon/components/icon/generator';
import Upload from '@webclient/components/generate-icon/components/upload';
import generateIconSchema from '@webclient/components/generate-icon/schema';
import {
  StyledBoxWrapper,
  StyledHeaderBox,
  StyledStackDescriptionField,
  StyledStackIcons,
  StyledStackWrapper,
  StyledUploadBox,
} from '@webclient/components/generate-icon/styles';
import { notification } from '@webclient/components/notification';
import FormProvider from '@webclient/components/react-hook-form/FormProvider';
import RHFInputField from '@webclient/components/react-hook-form/RHFInputField';

import useGenerateIcons from '@webclient/hooks/useGenerateIcons';
import useResponsive from '@webclient/hooks/useResponsive';

import { GenerateIconsRequestType } from '@webclient/shared-types/threads';

import SmartStarIcon from '~icons/knowz-iconify/smart-star';

import { faker } from '@faker-js/faker';
import { yupResolver } from '@hookform/resolvers/yup';
import { Text } from '@knowz/knowz-ui';
import { useTranslate } from '@tolgee/react';
import { isAxiosError } from 'axios';
import { useForm } from 'react-hook-form';
import { useSocket, useSocketEvent } from 'socket.io-react-hook';

type MessageType =
  | {
      type: 'imageGen';
      imageUrls: string[];
    }
  | {
      type: 'imageGen';
      error: string;
    };

const DESKTOP_MAX_ROWS = 7.25;
const MOBILE_MAX_ROWS = 5;

type Props = {
  Header?: React.ReactNode;
  Footer?: React.ReactNode;
  generatedIconsDescription: string;
  handleSetGeneratedIcon: (icon: string[]) => void;
  handleSetGeneratedIconDescription: (description: string) => void;
  selectedIcon: string;
  handleGoToStep: (step: string) => void;
  handleSetSelectedIcon: (icon: string) => void;
  generatedIcons: string[] | null;
  handleSetUploadedAvatar: (file: File) => void;
  handleSelectedUploadOption: (option: string) => void;
  generateImageParams: Omit<
    GenerateIconsRequestType,
    'threadId' | 'prompt' | 'total'
  >;
};

export default function GenerateIcon({
  generatedIconsDescription,
  handleSetGeneratedIcon,
  handleSetGeneratedIconDescription,
  selectedIcon,
  handleSetSelectedIcon,
  generatedIcons,
  Footer,
  Header,
  handleSetUploadedAvatar,
  handleGoToStep,
  handleSelectedUploadOption,
  generateImageParams,
}: Props) {
  const { t } = useTranslate();
  const [status, setStatus] = useState<
    'pending' | 'finished' | 'idle' | 'failed'
  >('idle');
  const [error, setError] = useState<string | null>(null);
  const {
    mutateAsync: mutateGenerateIconsAsync,
    isPending: areGenerateIconsAsyncPending,
  } = useGenerateIcons({
    onError: (error) => {
      if (
        isAxiosError(error) &&
        typeof error.code == 'number' &&
        error.code === 429
      ) {
        notification.error({
          message: t(
            'response.errors.code.429',
            'Too many requests. Please try again later.',
          ),
        });
        return;
      }
      notification.error({
        message: error.message,
      });
    },
  });

  const isItDesktop = useResponsive('up', 'md');

  const { socket } = useSocket(import.meta.env.VITE_KNOWZ_WEB_SOCKET_URL, {
    enabled: true,
    transports: ['websocket', 'polling'],
    withCredentials: true,
  });
  const methods = useForm({
    resolver: yupResolver(generateIconSchema(t)),
    defaultValues: {
      prompt: generatedIconsDescription,
    },
  });

  const { handleSubmit, watch } = methods;
  const isFieldEmpty = watch('prompt') === '';

  useSocketEvent(socket, 'message', {
    onMessage: async (message: MessageType) => {
      if (message.type === 'imageGen') {
        const areThereImageUrls = 'imageUrls' in message;
        if (areThereImageUrls) {
          handleSetGeneratedIcon(message.imageUrls);
          setStatus('finished');
          return;
        }
        if ('error' in message) {
          setStatus('failed');
          handleSetGeneratedIcon([]);
          setError(message.error);
          return;
        }
      }
    },
  });

  async function generateIcons({
    prompt,
  }: Pick<GenerateIconsRequestType, 'prompt'>) {
    if (status === 'pending' || areGenerateIconsAsyncPending) return;
    handleSetGeneratedIconDescription(prompt);
    const randomUUID = faker.string.uuid(); //? generate a random UUID for the thread no matter what
    socket.emit('subscribe', `thread_${randomUUID}`);
    await mutateGenerateIconsAsync({
      ...generateImageParams,
      prompt,
      total: import.meta.env.DEV ? 2 : 4,
      threadId: randomUUID,
    });
    setStatus('pending');
  }

  function handleDrop(file: File[]) {
    handleSetUploadedAvatar(file[0]);
    handleGoToStep('cropper');
  }

  function handleRedirectToGenerateIconStep() {
    handleSelectedUploadOption('upload');
    handleGoToStep('cropper');
  }

  return (
    <>
      {Header}
      <FormProvider
        methods={methods}
        onSubmit={handleSubmit(generateIcons)}
      >
        <StyledBoxWrapper>
          <StyledStackWrapper>
            <StyledStackDescriptionField
              direction="column"
              gap={2.5}
            >
              <Stack
                gap={3}
                sx={{ width: '100%' }}
              >
                <Stack
                  direction="row"
                  gap={2.5}
                >
                  <FeaturedIcon size="small">
                    <SmartStarIcon />
                  </FeaturedIcon>
                  <StyledHeaderBox>
                    <Text
                      variant="textMd"
                      weight="semibold"
                    >
                      {t(
                        'page.workspace.createWorkspaceModal.step2.subSteps.step1.generateIcon.segment1',
                        'Generate an icon',
                      )}
                    </Text>
                    <Text
                      variant="textSm"
                      color="text.disabled"
                    >
                      {t(
                        'page.workspace.createWorkspaceModal.step2.subSteps.step1.generateIcon.segment2',
                        'Describe what your icon to look like, this will use credits.',
                      )}
                    </Text>
                  </StyledHeaderBox>
                </Stack>

                <RHFInputField
                  multiline
                  maxRows={isItDesktop ? DESKTOP_MAX_ROWS : MOBILE_MAX_ROWS}
                  minRows={isItDesktop ? DESKTOP_MAX_ROWS : MOBILE_MAX_ROWS}
                  placeholder={t(
                    'page.workspace.createWorkspaceModal.step2.subSteps.step1.generateIcon.placeholder',
                    'Add description and press enter or just press generate and Knowz will create an image to use as your icon...',
                  )}
                  name="prompt"
                />
                {error && (
                  <Text
                    variant="textSm"
                    color="error"
                  >
                    {error}
                  </Text>
                )}
              </Stack>
              {isItDesktop && (
                <Upload
                  handleDrop={handleDrop}
                  handleRedirectToCropImageStep={
                    handleRedirectToGenerateIconStep
                  }
                />
              )}
            </StyledStackDescriptionField>
            <StyledStackIcons>
              <Stack
                direction={isItDesktop ? 'column' : 'row'}
                gap={1.5}
                sx={{
                  ...(isItDesktop
                    ? {
                        overflowY: 'auto',
                      }
                    : {
                        overflowX: 'auto',
                      }),
                }}
              >
                <Generator
                  handleSetSelectedIcon={handleSetSelectedIcon}
                  iconGenerationStatus={status}
                  selectedIcon={selectedIcon}
                  generatedIcons={generatedIcons}
                />
              </Stack>
              <Button
                fullWidth
                variant="contained"
                disabled={isFieldEmpty || status === 'pending'}
                onClick={handleSubmit(generateIcons)}
              >
                {t(
                  'page.workspace.createWorkspaceModal.step2.subSteps.step2.buttons.generate',
                  'Generate',
                )}
              </Button>
            </StyledStackIcons>
          </StyledStackWrapper>
        </StyledBoxWrapper>
      </FormProvider>
      {!isItDesktop && (
        <StyledUploadBox>
          <Upload
            handleDrop={handleDrop}
            handleRedirectToCropImageStep={handleRedirectToGenerateIconStep}
          />
        </StyledUploadBox>
      )}
      {Footer}
    </>
  );
}
